export const MOCK_POLICY_CACHE_KEY = 'geo_mock_policy'

export const VALID_MOCK_POLICIES = ['off', 'block']

export const DEFAULT_MOCK_POLICY = 'off'

/**
 * 'audit' existio en el primer diseno de la politica y nunca bloqueo ni midio nada: ningun consumidor
 * la distinguia de 'off'. Se lee como 'off' explicito (no depende de cual sea el default): una
 * organizacion que la tenia guardada nunca pidio bloquear. Cuando se implemente un modo de
 * solo-observacion de verdad (telemetria a Sentry) entrara con nombre propio.
 */
const LEGACY_MOCK_POLICIES = { audit: 'off' }

function normalizeMockPolicy (value) {
  if (typeof value !== 'string') return undefined

  // El alias se toma solo si es una cadena propia del mapa: indexar un objeto literal con una llave
  // heredada de Object.prototype ('constructor', 'toString') devuelve una funcion, no undefined.
  const key = value.toLowerCase()
  const alias = LEGACY_MOCK_POLICIES[key]
  const policy = typeof alias === 'string' ? alias : key

  return VALID_MOCK_POLICIES.includes(policy) ? policy : undefined
}

export function parseMockPolicy (raw) {
  let blob = raw

  if (typeof blob === 'string') {
    try {
      blob = JSON.parse(blob)
    } catch {
      return DEFAULT_MOCK_POLICY
    }
  }

  const settings = Array.isArray(blob) ? blob[0] : blob
  if (!settings || typeof settings !== 'object') return DEFAULT_MOCK_POLICY

  const value = settings.mock_location_enabled

  if (value === false || value === 'false') return 'off'
  if (value === true || value === 'true') return 'block'

  return normalizeMockPolicy(value) || DEFAULT_MOCK_POLICY
}

/**
 * La cache viaja con la organizacion que la escribio. En un dispositivo compartido que se cerro sin
 * pasar por el logout —crash, force-stop, LMK— el siguiente usuario arranca con esta cache intacta:
 * sin el sello de organizacion heredaria el 'off' de la organizacion anterior y su ubicacion
 * ficticia pasaria sin validacion. Una entrada de otra organizacion no opina y el device cae al
 * default (apagado) hasta que el server responda la politica de la organizacion actual, lo que
 * ocurre en el mismo login si hay red.
 */
export function readCachedMockPolicy (orgId) {
  if (orgId == null) return undefined

  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(MOCK_POLICY_CACHE_KEY) : null
    if (!raw) return undefined

    // Una entrada del formato viejo (la politica sola, sin organizacion) no se puede atribuir a
    // nadie: no opina y la siguiente respuesta del server la reescribe con sello.
    const entry = JSON.parse(raw)
    if (!entry || typeof entry !== 'object') return undefined
    if (String(entry.org) !== String(orgId)) return undefined

    return normalizeMockPolicy(entry.policy)
  } catch {
    return undefined
  }
}

function cacheMockPolicy (orgId, policy) {
  try {
    localStorage.setItem(MOCK_POLICY_CACHE_KEY, JSON.stringify({ org: String(orgId), policy }))
  } catch { /* sin localStorage: no-op */ }
}

export function clearCachedMockPolicy () {
  try {
    localStorage.removeItem(MOCK_POLICY_CACHE_KEY)
  } catch { /* sin localStorage: no-op */ }
}

export async function fetchMockPolicy (orgId, { fetchSettings } = {}) {
  if (orgId == null) return undefined

  try {
    const get = fetchSettings || (async id => {
      const { default: OrganizationService } = await import('src/services/organization')

      return OrganizationService.getGeneralSettings(id, 'extra_settings')
    })

    const response = await get(orgId)
    const policy = parseMockPolicy(response && response.data ? response.data.data : undefined)

    cacheMockPolicy(orgId, policy)

    return policy
  } catch {
    return undefined
  }
}
